import { AfterViewInit, Component, ElementRef, Input, OnInit, QueryList, ViewChild, ViewChildren, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';

import { LayerApi } from '../../../api/layer-api';
import { util } from '../../../classes/util';
import { BaseComponent } from '../../../components/base.component';
import { ESRI } from '../../../esri-loader-util';
import { InputConverter } from '../../../inputConverter';
import { LayerService } from '../../../services/layer.service';
import { SplitThemeService } from '../../../services/split-theme.service';
import { IMapcoreGroupLayer, IMapcoreMapImageLayer, IMCMapView, IQuery, isDynamicDataLayerSource, isGeometryDataSource, isGroupLayer, isMapImageLayer, ISplitThemeConfiguration, isSimpleRenderer, isSymbolWithOutline, isSymbolWithWidth, ISymbolChangeDetail, IThemeClassBreak, IThemeType, QueryableMapCoreLayer } from '../../../types';

let SymbolUtils: __esri.symbolUtils;

@Component({
  encapsulation: ViewEncapsulation.ShadowDom,
  selector: 'mc-split-theme-editor',
  templateUrl: './split-theme-editor.component.html',
  styleUrls: ['../../sharedStyles/base.scss', './split-theme-editor.component.scss']
})
export class SplitThemeEditorComponent extends BaseComponent implements AfterViewInit, OnInit {

  windowRef: any;
  symbolEditorWindow;
  symbolEditor;

  @ViewChild('fieldSelect') fieldSelect: ElementRef<HTMLSelectElement>;
  @ViewChildren('symbolPreview') symbolPreviews: QueryList<ElementRef<HTMLDivElement>>;

  @Input()
  mapViewRef: IMCMapView;

  @Input()
  layerId: string;

  @Input()
  @InputConverter()
  useEsriScheme: boolean = false;

  @Input()
  @InputConverter()
  useSymbolScheme: boolean = false;

  DEFAULT_NUM_CLASSES = 5;

  originalLayer: IMapcoreMapImageLayer;
  selectedField: string;
  layerColumns: { name: string; alias: string }[];
  theme?: ISplitThemeConfiguration;

  errorMessage: string;
  loadingData = false;

  wasOpen = false;
  symbolPreviewsSub: Subscription;
  recreateGroupLayer: boolean = false;
  attemptedRendererType;
  classBreakToAdd: string;
  classBreakToEdit: string;
  newMinValue: number;
  newMaxValue: number;
  changedColorClassBreaks: string[];
  currentSymbol: string;
  applyColorToEntireLayer: boolean = false;
  groupParent: any;

  get displayRendererType() {
    return (this.errorMessage && this.attemptedRendererType) || (this.theme && this.theme.rendererType);
  }

  private get selectedClassBreaks() {
    return this.theme.classBreaks.filter(cb => cb.selected);
  }

  availableClassBreaks: IThemeClassBreak[] = [];

  constructor(vcr: ViewContainerRef,
              private readonly layerService: LayerService,
              private readonly layerApi: LayerApi,
              private readonly splitThemeService: SplitThemeService) {
    super(vcr, true);
  }

  async ngOnInit() {
    SymbolUtils = await ESRI.SymbolUtils;

    this.symbolEditorWindow = window.document.getElementById('symbolEditorWindow');
    this.symbolEditor = window.document.getElementById('symbolEditor');

    window.addEventListener('newSymbols', async (e: CustomEvent<ISymbolChangeDetail>) => this.updateThemeSymbols(e));

    this.getClosestAncestor('mc-window').then((window: any) => {
      if (window) {
        this.windowRef = window;
        this.windowRef.addEventListener('openChange', open => {
          if (open.detail) {
            this.windowOpened();
          } else {
            this.destroyComponent();
          }
          this.wasOpen = open.detail;
        });
      } else {
        this.windowOpened();
      }
    });
  }

  watchSymbolPreviewChanges() {
    this.symbolPreviewsSub = this.symbolPreviews.changes.subscribe(async _ => await this.renderSymbols());
  }

  unwatchSymbolPreviewChanges() {
    if (this.symbolPreviewsSub) {
      this.symbolPreviewsSub.unsubscribe();
      this.symbolPreviewsSub = null;
    }
  }

  async ngAfterViewInit() {
    this.mapViewRef = this.mapViewRef ? this.mapViewRef : await this.setupMapViewRef(this.mapViewRef);
    this.splitThemeService.mapView = this.mapViewRef;
    this.isReady();
  }

  windowOpened() {
    const layer = this.layerService.getEsriLayerByUniqueId(this.layerId);

    this.classBreakToAdd = null;
    this.changedColorClassBreaks = [];

    if (layer.mapcoreProps && layer.mapcoreProps.splitTheme && !isGroupLayer((<any>layer).parent)) {
      this.loadTheme(layer.mapcoreProps.splitTheme);
    } else if (isMapImageLayer(layer)) {
      this.originalLayer = layer;
      this.generateClassBreaks('unique-value', true, layer);
    } else {
      throw new Error('Unsupported layer type for Theme Editor');
    }

    if (!this.wasOpen) {
      this.watchSymbolPreviewChanges();
    }
  }

  async closeWindow(save: boolean) {
    this.unwatchSymbolPreviewChanges();

    if (save) {
      await this.applyTheme();
      this.updateLayersFromTheme();
      this.saveTheme();
    }

    if (this.windowRef) {
        this.windowRef.close();
      }
  }

  async renderSymbols() {
    if (!this.theme) {
      return;
    }

    for (const symbolContainer of this.symbolPreviews.toArray()) {
      for (const child of Array.from(symbolContainer.nativeElement.childNodes)) {
        symbolContainer.nativeElement.removeChild(child);
      }

      const classBreak = this.theme.classBreaks.find(cb => cb.info.label === symbolContainer.nativeElement.dataset.label);

      for (const symbol of classBreak.symbols) {
        const symbolWithLayerOpacity = util.deepClone(symbol);
        symbolWithLayerOpacity.color.a *= classBreak.opacity;
        if ((<any>symbolWithLayerOpacity).outline) {
          (<any>symbolWithLayerOpacity).outline.color.a *= classBreak.opacity;
        }
        await SymbolUtils.renderPreviewHTML(symbolWithLayerOpacity, { maxSize: 14, scale: true, node: symbolContainer.nativeElement, size: { width: 29 } });
      }
    }
  }

  /**
   * Loads a copy of a theme into the editor.
   * @param theme The theme to load.
   */
  loadTheme(theme: ISplitThemeConfiguration) {
    this.layerColumns = theme.layerColumns;
    this.selectedField = theme.field;
    this.availableClassBreaks = theme.classBreaks.filter(cb => !cb.selected);

    if (theme.rendererType === 'unique-value') {
      // Reorder the class breaks in case their layers have been moved, and make copies of them so the changes can be cancelled

      const classBreaks = theme.classBreaks
        .filter(cb => !cb.selected)
        .map(cb => this.copyClassBreak(cb));

      for (const layer of theme.groupLayer.layers.toArray().reverse()) {
        const classBreak = theme.classBreaks.find(cb => cb.info.label === layer.mapcoreProps.splitThemeLabel);
        classBreaks.push(this.copyClassBreak(classBreak));
      }

      this.theme = { ...theme, classBreaks };
    } else {
      this.theme = { ...theme, classBreaks: theme.classBreaks.map(cb => this.copyClassBreak(cb)) };
    }
  }

  copyClassBreak(classBreak: IThemeClassBreak) {
    return {
      ...classBreak,
      info: { ...classBreak.info } as __esri.ClassBreakInfo,
      symbols: classBreak.symbols.map((sym: any) => sym.clone())
    };
  }

  saveTheme() {
    this.theme.groupLayer.mapcoreProps.splitTheme = this.theme;
    this.theme.groupLayer.layers.forEach(gl => gl.mapcoreProps.splitTheme = this.theme);
  }

  toggleRendererType() {
    if (this.displayRendererType === 'unique-value') {
      this.attemptedRendererType = 'class-break';
      this.refreshTheme('class-break');
    } else {
      this.attemptedRendererType = 'unique-value';
      this.refreshTheme('unique-value');
    }

  }

  async initializeTheme(layer: IMapcoreMapImageLayer) {
    const layerId = this.theme ? this.theme.parentLayerId : layer.mapcoreProps.id || layer.mapcoreProps.parentId;
    const query = this.theme ? this.theme.query : (<any>layer.sublayers.getItemAt(0)).source.dataSource.query;

    const recordCount = await this.layerApi.getLayerRecordCount(layerId, JSON.parse(query));

    if (!recordCount || recordCount.length === 0 || recordCount[0].recordCount === 0) {
      this.loadingData = false;
      return;
    }

    let selectedColumn;

    if (!this.theme) {
      const currentSubLayer = layer.sublayers.getItemAt(0);

      if (!isDynamicDataLayerSource(currentSubLayer.source) || !isGeometryDataSource(currentSubLayer.source.dataSource)) {
        return false;
      }

      this.layerColumns = this.splitThemeService.getLayerColumnsFromLayer(layer);
      this.selectedField = this.layerColumns[0].name;

      this.theme = {
        parentLayerId: layer.mapcoreProps.parentId,
        originalOpacity: layer.opacity,
        parentQuery: layer.mapcoreProps.parentQuery,
        childQuery: layer.mapcoreProps.query,
        visualVariables: this.layerService.getRendererDefinitionsForLayer(layer)[0].visualVariables,
        baseSymbols: layer.sublayers.toArray().map(l => (<__esri.SimpleRenderer>l.renderer).symbol),
        layerColumns: this.layerColumns,
        field: this.layerColumns[0].name,
        isDate: false,
        isNumeric: false,
        query,
        rendererType: 'unique-value',
        numClasses: this.DEFAULT_NUM_CLASSES,
        classBreaks: []
      };
    }

    selectedColumn = this.theme.layerColumns.find(x => x.name === this.selectedField);
    this.theme.field = this.selectedField;
    this.theme.isDate = selectedColumn && selectedColumn.databaseDataType && selectedColumn.name.toUpperCase().includes('DATE');
  }

  async generateClassBreaks(rendererType: IThemeType, changeField: boolean = false, layer: IMapcoreMapImageLayer = null) {
    this.errorMessage = '';
    this.loadingData = true;
    await this.initializeTheme(layer);

    let result = await this.splitThemeService.getLayerClassBreakData(
      this.theme.parentLayerId,
      this.theme.field,
      this.theme.numClasses,
      rendererType === 'unique-value',
      JSON.parse(this.theme.query),
      this.theme.isDate
    );

    if (changeField && rendererType === 'unique-value' && (result.IsNumericData || this.theme.isDate)) {
      rendererType = 'class-break';

      if (this.splitThemeService.validateClassBreakCount(rendererType, result.ClassBreakInfos.length)) {
        this.theme.numClasses = this.DEFAULT_NUM_CLASSES;
      }

      result = await this.splitThemeService.getLayerClassBreakData(
        this.theme.parentLayerId,
        this.theme.field,
        this.theme.numClasses,
        false,
        JSON.parse(this.theme.query),
        this.theme.isDate
      );
    }

    const validationMessage = this.splitThemeService.validateClassBreakCount(rendererType, result.ClassBreakInfos.length);

    if (validationMessage) {
      this.loadingData = false;
      this.errorMessage = validationMessage;
      return;
    }

    const previousColors = this.theme.classBreaks ? this.theme.classBreaks.map(cb => cb.symbols[0].color) : [];
    const colorSets = (await this.splitThemeService.getClassColors(
      result.ClassBreakInfos.length,
      previousColors,
      this.theme.baseSymbols[0],
      this.useSymbolScheme,
      this.useEsriScheme))[0];

    const resetClassBreaks = changeField || rendererType === 'class-break' || rendererType !== this.theme.rendererType;
    const previousClassBreakCount = resetClassBreaks ? 0 : this.theme.classBreaks.length;
    this.theme.isNumeric = result.IsNumericData;

    for (let i = previousClassBreakCount; i < result.ClassBreakInfos.length; i++) {
      this.theme.classBreaks[i] = {
        selected: true,
        info: result.ClassBreakInfos[i],
        layer: null,
        opacity: this.theme.originalOpacity,
        symbols: this.theme.baseSymbols.map(bs => {
          const symbol = util.deepClone(bs);
          symbol.color = colorSets[i];
          if (isSymbolWithOutline(symbol)) {
            symbol.outline.color = colorSets[i];
          }
          return symbol;
        })
      };
    }

    this.theme.classBreaks.length = result.ClassBreakInfos.length;
    this.theme.rendererType = rendererType;
    this.loadingData = false;
  }

  async refreshTheme(rendererType: IThemeType = null, changeField = false) {
    return await this.generateClassBreaks(rendererType || this.theme.rendererType, changeField);
  }

  async changeField() {
    this.recreateGroupLayer = true;
    this.attemptedRendererType = null;
    await this.refreshTheme('unique-value', true);
  }

  /**
   * Creates and performs updates on the theme group layer.
   */
  async applyTheme() {
    this.loadingData = true;

    const groupParent = this.configureGroupLayer();

    for (const classBreak of this.selectedClassBreaks) {
      const layerConfiguration = groupParent.layers.find(lc => lc.label === classBreak.info.label);

      layerConfiguration.renderers = classBreak.symbols.map(symbol => ({
        type: 'simple',
        visualVariables: this.theme.visualVariables,
        symbol
      }));
    }

    const selectedLabels = this.selectedClassBreaks.map(cb => cb.info.label);
    const originalLayerIndex = this.theme.groupLayer && this.mapViewRef.map.layers.indexOf(this.theme.groupLayer);

    if (!this.theme.groupLayer || this.recreateGroupLayer) {
      // Create the group layer, or recreate it if the number of class breaks has changed

      if (this.theme.groupLayer) {
        this.layerService.removeLayer(this.theme.groupLayer);
      }

      const layers = await this.layerService.addMapcoreLayersToMap(groupParent.layers);
      this.theme.groupLayer = layers.find(layer => layer.type === 'group') as IMapcoreGroupLayer;

      for (const [i, themeLayer] of this.theme.groupLayer.layers.toArray().entries()) {
        const classBreak = this.theme.classBreaks.find(cb => cb.info.label === selectedLabels[i]);
        classBreak.layer = themeLayer as QueryableMapCoreLayer;
        themeLayer.mapcoreProps.splitTheme = this.theme;
        themeLayer.mapcoreProps.splitThemeLabel = selectedLabels[i];
      }

      this.theme.groupLayer.layers.reverse();
      this.theme.groupLayer.mapcoreProps.splitTheme = this.theme;

      if (this.originalLayer) {
        this.layerService.moveLayerBelow(this.theme.groupLayer, this.originalLayer);
        this.originalLayer.visible = false;
      } else {
        this.mapViewRef.map.layers.reorder(this.theme.groupLayer, originalLayerIndex);
      }
    } else if (this.theme.groupLayer) {
      // Update the existing theme layers

      const previouslySelectedLabels = this.theme.groupLayer.layers.map(gl => gl.mapcoreProps.splitThemeLabel);
      const addLayers = groupParent.layers.filter(lc => !previouslySelectedLabels.includes(lc.label));
      const removeLayers = this.theme.groupLayer.layers.toArray().filter(gl => !selectedLabels.includes(gl.mapcoreProps.splitThemeLabel));
      let updatedLayers = false;

      for (const classBreak of this.theme.classBreaks.filter(cb => cb.layer)) {
        classBreak.layer.opacity = classBreak.opacity;

        if (this.theme.rendererType === 'class-break') {
          // Update layer titles and queries to reflect new class breaks

          const query = classBreak.layer.mapcoreProps.query;
          const rangeQuery = this.findRangeQuery(query);
          const minValue = classBreak.info.minValue.toString();
          const maxValue = classBreak.info.maxValue.toString();

          if (rangeQuery.subqueries[0].value !== minValue || rangeQuery.subqueries[1].value !== maxValue) {
            rangeQuery.subqueries[0].value = minValue;
            rangeQuery.subqueries[1].value = maxValue;

            await this.layerService.updateLayerFilter(classBreak.layer, query);
            classBreak.layer.mapcoreProps.name = groupParent.layers.find(gl => gl.label === classBreak.info.label).name;
            updatedLayers = true;
          }
        }
      }

      for (const removedLayer of removeLayers) {
        this.theme.groupLayer.remove(removedLayer);
      }

      if (addLayers.length > 0) {
        const layers = await this.layerService.addMapcoreLayersToMap(addLayers);
        const tempGroup = layers.find(layer => layer.type === 'group') as IMapcoreGroupLayer;

        for (const [i, addedLayer] of tempGroup.layers.toArray().entries()) {
          const classBreak = this.theme.classBreaks.find(cb => cb.info.label === addLayers[i].label);
          classBreak.layer = addedLayer as QueryableMapCoreLayer;

          addedLayer.mapcoreProps.groupParent.groupId = this.theme.groupLayer.id;
          addedLayer.mapcoreProps.splitTheme = this.theme;
          addedLayer.mapcoreProps.splitThemeLabel = addLayers[i].label;
          this.theme.groupLayer.add(addedLayer);
        }

        this.layerService.removeLayer(tempGroup);
      }

      for (const [i, classBreak] of this.theme.classBreaks.entries()) {
        if (classBreak.layer) {
          this.theme.groupLayer.layers.reorder(classBreak.layer, this.selectedClassBreaks.length - i - 1);
        }
      }

      if (updatedLayers && removeLayers.length === 0 && addLayers.length === 0) {
        // Trigger a layer refresh in case the layer titles changed
        this.layerService.refreshLayers();
      }
    }

    this.loadingData = false;
  }

  configureGroupLayer() {
    const groupParent = {
      layers: [],
      name: this.originalLayer
        ? this.originalLayer.mapcoreProps.name
        : this.theme.groupLayer.mapcoreProps.name,
      checked: true,
      isOpen: true
    };

    const query = this.theme.childQuery;
    const currentQuery = query.subqueries ? query.subqueries : [query];

    for (const [i, classBreak] of this.selectedClassBreaks.entries()) {
      const info = classBreak.info;
      let label = '';
      const subquery = [];
      if (this.layerService.isProperQuery(query)) {
          subquery.push(...currentQuery);
      }

      if (this.theme.rendererType === 'unique-value') {
        label = info.label;
        subquery.push(this.buildUniqueQuery(info, this.theme.field));
      } else {
        label = `${info.minValue} < ${info.maxValue}`;
        const isLastBreak = (i === this.selectedClassBreaks.length - 1);
        subquery.push(this.buildRangeQuery(info, this.theme.field, isLastBreak));
      }

      const selectedColumn = this.theme.layerColumns.find(lc => lc.name === this.theme.field);

      groupParent.layers.push({
          id: this.theme.parentLayerId,
          label: classBreak.info.label,
          visible: true,
          groupParent,
          name: `${selectedColumn && selectedColumn.alias ? selectedColumn.alias : this.theme.field} (${label})`,
          query: {
              operator: 'AND',
              subqueries: subquery
          },
          opacity: classBreak.opacity,
          parentQuery: this.theme.parentQuery
      });
    }

    return groupParent;
  }

  buildRangeQuery(classBreak: any, filterField: string, lastBreak: boolean = false) {
    return {
        name: 'classBreak',
        operator: 'AND',
        subqueries: [
            {
                field: filterField,
                operator: '>=',
                value: classBreak.minValue.toString()
            },
            {
                field: filterField,
                operator: lastBreak ? '<=' : '<',
                value: classBreak.maxValue.toString()
            }
        ]
    };
  }

  buildUniqueQuery(info: any, filterField: string) {
      const val = info.value === null ? 'null' : info.value.toString();
      return {
          operator: 'AND',
          isHiddenFilter: true,
          subqueries: [
              {
                  field: filterField,
                  operator: val.toLowerCase() === 'null' ? 'IS NULL' : '=',
                  value: val.toLowerCase() === 'null' ? null : val
              }
          ]
      };
  }

  findRangeQuery(query: IQuery) {
    const queries = [query];

    while (queries.length > 0) {
      const current = queries.pop();

      if (current.name === 'classBreak') {
        return current;
      }

      if (current.operator === 'AND' || current.operator === 'OR') {
        for (const subquery of current.subqueries) {
          queries.push(subquery);
        }
      }
    }

    return null;
  }

  async resetColors() {
    const colorSets = (await this.splitThemeService.getClassColors(
      this.theme.classBreaks.length,
      [],
      this.theme.baseSymbols[0],
      this.useSymbolScheme,
      this.useEsriScheme))[0];

    for (const [i, classBreak] of this.theme.classBreaks.entries()) {
      this.changedColorClassBreaks.push(classBreak.info.label);

      for (const symbol of classBreak.symbols) {
        symbol.color = colorSets[i];

        if (isSymbolWithOutline(symbol)) {
          symbol.outline.color = colorSets[i];
        }
      }
    }

    await this.renderSymbols();
  }

  async stackLineWidths() {
    const firstClassBreak = this.theme.classBreaks.find(x => x.selected);
    const firstLineSymbol = firstClassBreak.symbols.find(isSymbolWithWidth);

    if (!firstLineSymbol) {
      return;
    }

    let lineWidth = firstLineSymbol.width;

    for (const classBreak of this.selectedClassBreaks.slice(1)) {
      lineWidth += 3;
      classBreak.symbols.find(isSymbolWithWidth).width = lineWidth;
      this.changedColorClassBreaks.push(classBreak.info.label);
    }

    await this.renderSymbols();
  }

  editSymbol(classBreak) {
    if (this.symbolEditorWindow) {
      this.symbolEditorWindow.open();
      this.symbolEditor.symbols = classBreak.symbols.map(s => util.deepClone(s));
      this.symbolEditor.opacity = classBreak.opacity * 100;

      this.currentSymbol = classBreak.info.label;
      this.changedColorClassBreaks.push(classBreak.info.label);
    }
  }

  editClassBreak(classBreak) {
    this.newMinValue = classBreak.info.minValue;
    this.newMaxValue = classBreak.info.maxValue;
    this.classBreakToEdit = classBreak.info.label;
  }

  saveClassBreak(classBreak: IThemeClassBreak) {
    const index = this.theme.classBreaks.indexOf(classBreak);
    const previousClassBreak = this.theme.classBreaks[index - 1];
    const nextClassBreak = this.theme.classBreaks[index + 1];

    if (previousClassBreak && this.newMinValue < previousClassBreak.info.minValue) {
      this.errorMessage = `${this.newMinValue} is less than the previous class break's minimum of ${previousClassBreak.info.minValue}`;
      return;
    } else if (nextClassBreak && this.newMaxValue > nextClassBreak.info.maxValue) {
      this.errorMessage = `${this.newMaxValue} is greater than the next class break's maximum of ${nextClassBreak.info.maxValue}`;
      return;
    } else {
      this.errorMessage = '';
    }

    classBreak.info.minValue = this.newMinValue;
    classBreak.info.maxValue = this.newMaxValue;

    if (previousClassBreak) {
      previousClassBreak.info.maxValue = this.newMinValue;
    }

    if (nextClassBreak) {
      nextClassBreak.info.minValue = this.newMaxValue;
    }

    this.classBreakToEdit = null;
  }

  cancelEditClassBreak() {
    this.errorMessage = '';
    this.classBreakToEdit = null;
  }

  editClassBreakKeyUp(classBreak, e) {
    if (e.code === 'Enter') {
      this.saveClassBreak(classBreak);
    } else if (e.code === 'Escape') {
      this.cancelEditClassBreak();
    }
  }

  addClassBreak() {
    if (this.theme.rendererType === 'unique-value') {
      const classBreak = this.theme.classBreaks.find(cb => cb.info.label === this.classBreakToAdd);
      classBreak.selected = true;
      this.classBreakToAdd = null;
      this.configureGroupLayer();
      this.availableClassBreaks = this.theme.classBreaks.filter(cb => !cb.selected);
    } else {
      this.theme.numClasses += 1;
      this.recreateGroupLayer = true;
      this.refreshTheme();
    }
  }

  removeClassBreak(classBreak) {
    if (this.theme.rendererType === 'unique-value') {
      classBreak.selected = false;
      this.classBreakToAdd = null;
      this.configureGroupLayer();
      this.availableClassBreaks = this.theme.classBreaks.filter(cb => !cb.selected);
    } else {
      this.theme.classBreaks.splice(this.theme.numClasses - 1, 1);
      this.theme.numClasses -= 1;
      this.recreateGroupLayer = true;
      this.refreshTheme();
    }
  }

  moveValueUp(classBreak) {
    const index = this.theme.classBreaks.indexOf(classBreak);
    this.theme.classBreaks.splice(index, 1);

    for (let i = index - 1; i >= 0; i--) {
      if (this.theme.classBreaks[i].selected) {
        this.theme.classBreaks.splice(i, 0, classBreak);
        break;
      }
    }
  }

  moveValueDown(classBreak) {
    const index = this.theme.classBreaks.indexOf(classBreak);
    this.theme.classBreaks.splice(index, 1);

    for (let i = index + 1; i <= this.theme.classBreaks.length; i++) {
      if (i === this.theme.classBreaks.length || this.theme.classBreaks[i - 1].selected) {
        this.theme.classBreaks.splice(i, 0, classBreak);
        break;
      }
    }
  }

  /**
   * Updates the theme when changing a symbol.
   * @param e The newSymbol event. If layerId is null, the symbols currently being edited from the theme editor are being changed.
   *          If layerId is not null, the specified layer's symbols are being changed from the legend.
   */
  async updateThemeSymbols(e: CustomEvent<ISymbolChangeDetail>) {
    let theme: ISplitThemeConfiguration;
    let label: string;

    if (e.detail.layerId) {
      const layer = this.layerService.getExistingEsriLayerById(e.detail.layerId);
      theme = layer.mapcoreProps.splitTheme;
      label = layer.mapcoreProps.splitThemeLabel;
    } else {
      theme = this.theme;
      label = this.currentSymbol;
    }

    if (!theme) {
      return;
    }

    const classBreak = theme.classBreaks.find(cb => cb.info.label === label);
    classBreak.opacity = e.detail.opacity / 100;

    for (const [i, newSymbol] of e.detail.symbols.entries()) {
      if (!newSymbol) {
        break;
      }

      classBreak.symbols[i] = newSymbol;
    }

    if (this.symbolEditor.applyColorToEntireLayer) {
      for (let i = e.detail.symbols.length; i < classBreak.symbols.length; i++) {
        classBreak.symbols[i].color = e.detail.symbols[0].color;
      }
    }

    if (this.windowRef.isOpened) {
      await this.renderSymbols();
    }
  }

  /**
   * Updates theme layer renderers with the colors selected in the theme editor.
   */
  updateLayersFromTheme() {
    for (const classBreak of this.theme.classBreaks.filter(cb => this.changedColorClassBreaks.includes(cb.info.label))) {
      if (!classBreak.layer || !isMapImageLayer(classBreak.layer)) {
        continue;
      }

      for (const [j, symbol] of classBreak.symbols.entries()) {
        const sublayer = classBreak.layer.allSublayers.getItemAt(j);

        if (isSimpleRenderer(sublayer.renderer)) {
          sublayer.renderer.symbol = symbol;
        }
      }

      window.dispatchEvent(new CustomEvent<string>('symbolChange', { detail: classBreak.layer.id }));
    }
  }

  destroyComponent() {
    this.unwatchSymbolPreviewChanges();
    this.errorMessage = null;
    this.loadingData = false;
    this.recreateGroupLayer = false;
    this.theme = null;
    this.originalLayer = null;
    this.availableClassBreaks = [];
  }
}
